import { useQuery } from "@tanstack/react-query";
import { getOrderStats } from "@/services/orders";
import { listPendingDriverDispatches } from "@/services/dispatch";
import { sitePaymentsApi } from "@/services/sitePayments";
import { openApiAdmin } from "@/services/openApi";
import type { NavItem } from "./navConfig";

/**
 * 侧栏 / 顶部模块导航共用的待办徽标：各模块 badge 各管各的领域（原来写在 Sidebar.tsx 里，THEME-1 抽出来给两套版式用，逻辑一字未改）。
 * 返回 badgeFor(item)；没有徽标 = undefined。
 */
export function useNavBadges(items: NavItem[]): (item: NavItem) => number | undefined {
  // 订单中心 badge = 待派（confirmed·已确认待分配资源）· OP 订单运营待办·对所有角色一致。
  // 不含待结算（那是财务模块领域·各模块 badge 各管各的领域·不跨角色拼凑·避免冲突）。
  const ordersVisible = items.some((i) => i.id === "orders");
  const statsQ = useQuery({
    queryKey: ["orders", "stats"],
    queryFn: () => getOrderStats(),
    enabled: ordersVisible,
    staleTime: 30_000,
  });
  const ordersAwaitingDispatch = statsQ.data?.confirmed;

  // BATCH-2 屏 04 · 调度中心 badge = 待补司导（车已定·司导未定）——同款写法，
  // 各模块 badge 各管各的领域，不跨角色/跨模块拼凑。
  const dispatchVisible = items.some((i) => i.id === "dispatch");
  const pendingDriverQ = useQuery({
    queryKey: ["dispatch", "pending-driver"],
    queryFn: () => listPendingDriverDispatches(),
    enabled: dispatchVisible,
    staleTime: 30_000,
  });
  const pendingDriverCount = pendingDriverQ.data?.length;

  // PAY-1 · 官网收款：订单中心下的子项 = 待初筛数；财务下的子项 = 待查账 + 异常数（同一页两个入口）
  const payOrdersVisible = items.some((i) => i.id === "site-payments-orders");
  const payFinanceVisible = items.some((i) => i.id === "site-payments-finance");
  const payCountsQ = useQuery({
    queryKey: ["site-payments", "counts"],
    queryFn: sitePaymentsApi.counts,
    enabled: payOrdersVisible || payFinanceVisible,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  // OPENAPI-2 · 开放平台审批：待审接入申请 + 待发正式密钥的上线申请
  const openApiVisible = items.some((i) => i.id === "open-api-approvals");
  const openApiQ = useQuery({
    queryKey: ["open-api", "pending-counts"],
    queryFn: openApiAdmin.pendingCounts,
    enabled: openApiVisible,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const badgeFor = (item: NavItem): number | undefined =>
    item.id === "site-payments-orders" ? payCountsQ.data?.screen
    : item.id === "site-payments-finance" ? (payCountsQ.data ? payCountsQ.data.audit + payCountsQ.data.exception : undefined)
    : item.id === "open-api-approvals" ? (openApiQ.data ? openApiQ.data.applications + openApiQ.data.go_live : undefined)
    : item.id === "orders" ? ordersAwaitingDispatch
    : item.id === "dispatch" ? pendingDriverCount
    : item.badge;

  return badgeFor;
}
